/**
 * A passkey's answer for a login's second step (decision 53; part 29b, PK4; part 36a, LG5): the relay's options turned
 * into WebAuthn's, the browser asked (`navigator.credentials.get`), and its credential written as the JSON the relay
 * reads (`assertionOf`: base64url fields, no padding). From part 36c a new passkey too (FX3, FX6; PK3): the relay's
 * creation prompt, the browser asked (`navigator.credentials.create`), and its credential written as the JSON the relay
 * reads (`registrationOf`). The browser's objects are read by shape, so this is a boundary; a prompt the writer cancels,
 * or anything that doesn't read, is null.
 */
import { fromBase64Url, toBase64Url } from './bytes.ts';
import type { PasskeyCreation, PasskeyRequest } from './wire.ts';

/** The browser's credentials container, as far as used (`navigator.credentials`). */
export interface CredentialsContainer {
  get(options: CredentialRequestOptions): Promise<unknown>;
  create(options: CredentialCreationOptions): Promise<unknown>;
}

/** The transports WebAuthn names; the relay's others are dropped (a hint only). */
const TRANSPORTS: readonly AuthenticatorTransport[] = ['ble', 'hybrid', 'internal', 'nfc', 'usb'];

/** Bytes of an `ArrayBuffer` (or a view of one), base64url; null for anything else. */
function encoded(value: unknown): string | null {
  if (value instanceof ArrayBuffer) return toBase64Url(new Uint8Array(value));
  if (ArrayBuffer.isView(value)) return toBase64Url(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
  return null;
}

/** The relay's options as WebAuthn's, or null when one of their byte fields doesn't read. */
export function requestOptions(request: PasskeyRequest): PublicKeyCredentialRequestOptions | null {
  const challenge = fromBase64Url(request.challenge);
  const allowCredentials: PublicKeyCredentialDescriptor[] = [];
  for (const credential of request.allowCredentials) {
    const id = fromBase64Url(credential.id);
    if (id === null) return null;
    allowCredentials.push({ type: 'public-key', id, transports: TRANSPORTS.filter((t) => credential.transports.includes(t)) });
  }
  if (challenge === null) return null;
  return {
    challenge,
    allowCredentials,
    ...(request.rpId === null ? {} : { rpId: request.rpId }),
    ...(request.timeout === null ? {} : { timeout: request.timeout }),
    ...(request.userVerification === null ? {} : { userVerification: request.userVerification }),
  };
}

/** The credential the browser gave, as the relay reads an assertion; null for anything else. */
export function assertionJson(credential: unknown): Record<string, unknown> | null {
  if (typeof credential !== 'object' || credential === null) return null;
  const response: unknown = Reflect.get(credential, 'response');
  const rawId = encoded(Reflect.get(credential, 'rawId'));
  const attachment: unknown = Reflect.get(credential, 'authenticatorAttachment');
  if (typeof response !== 'object' || response === null || rawId === null || Reflect.get(credential, 'type') !== 'public-key') return null;
  const clientDataJSON = encoded(Reflect.get(response, 'clientDataJSON'));
  const authenticatorData = encoded(Reflect.get(response, 'authenticatorData'));
  const signature = encoded(Reflect.get(response, 'signature'));
  const handle: unknown = Reflect.get(response, 'userHandle');
  const userHandle = handle === null || handle === undefined ? null : encoded(handle);
  if (clientDataJSON === null || authenticatorData === null || signature === null || (handle !== null && handle !== undefined && userHandle === null)) return null;
  return {
    id: rawId,
    rawId,
    type: 'public-key',
    clientExtensionResults: {},
    response: { clientDataJSON, authenticatorData, signature, ...(userHandle === null ? {} : { userHandle }) },
    ...(attachment === 'platform' || attachment === 'cross-platform' ? { authenticatorAttachment: attachment } : {}),
  };
}

/** LG5: asks the browser for a passkey's answer to `request`; null when there is no container, the writer cancels, or it doesn't read. */
export async function askPasskey(credentials: CredentialsContainer | undefined, request: PasskeyRequest): Promise<Record<string, unknown> | null> {
  const publicKey = requestOptions(request);
  if (credentials === undefined || publicKey === null) return null;
  try {
    return assertionJson(await credentials.get({ publicKey }));
  } catch {
    return null;
  }
}

/** FX3: the relay's creation prompt as WebAuthn's, or null when one of its byte fields doesn't read. */
export function creationOptions(creation: PasskeyCreation): PublicKeyCredentialCreationOptions | null {
  const challenge = fromBase64Url(creation.challenge);
  const userId = fromBase64Url(creation.user.id);
  const excludeCredentials: PublicKeyCredentialDescriptor[] = [];
  for (const credential of creation.excluded) {
    const id = fromBase64Url(credential);
    if (id === null) return null;
    excludeCredentials.push({ type: 'public-key', id });
  }
  if (challenge === null || userId === null) return null;
  const selection: AuthenticatorSelectionCriteria = {
    ...(creation.residentKey === null ? {} : { residentKey: creation.residentKey, requireResidentKey: creation.residentKey === 'required' }),
    ...(creation.userVerification === null ? {} : { userVerification: creation.userVerification }),
  };
  return {
    challenge,
    rp: { name: creation.rp.name, ...(creation.rp.id === null ? {} : { id: creation.rp.id }) },
    user: { id: userId, name: creation.user.name, displayName: creation.user.displayName },
    pubKeyCredParams: creation.algorithms.map((alg) => ({ type: 'public-key', alg })),
    excludeCredentials,
    authenticatorSelection: selection,
    attestation: 'none',
    ...(creation.timeout === null ? {} : { timeout: creation.timeout }),
  };
}

/** The transports a new credential says it uses (`getTransports`), those WebAuthn names only; none when it can't say. */
function transportsOf(response: object): AuthenticatorTransport[] {
  const get: unknown = Reflect.get(response, 'getTransports');
  if (typeof get !== 'function') return [];
  let given: unknown;
  try {
    given = Reflect.apply(get, response, []);
  } catch {
    return [];
  }
  return Array.isArray(given) ? TRANSPORTS.filter((t) => given.includes(t)) : [];
}

/** FX3: the credential the browser made, as the relay reads a registration; null for anything else. */
export function registrationJson(credential: unknown): Record<string, unknown> | null {
  if (typeof credential !== 'object' || credential === null) return null;
  const response: unknown = Reflect.get(credential, 'response');
  const rawId = encoded(Reflect.get(credential, 'rawId'));
  const attachment: unknown = Reflect.get(credential, 'authenticatorAttachment');
  if (typeof response !== 'object' || response === null || rawId === null || Reflect.get(credential, 'type') !== 'public-key') return null;
  const clientDataJSON = encoded(Reflect.get(response, 'clientDataJSON'));
  const attestationObject = encoded(Reflect.get(response, 'attestationObject'));
  if (clientDataJSON === null || attestationObject === null) return null;
  return {
    id: rawId,
    rawId,
    type: 'public-key',
    clientExtensionResults: {},
    response: { clientDataJSON, attestationObject, transports: transportsOf(response) },
    ...(attachment === 'platform' || attachment === 'cross-platform' ? { authenticatorAttachment: attachment } : {}),
  };
}

/** FX3: asks the browser to make a passkey for `creation`; null when there is no container, the writer cancels, or it doesn't read. */
export async function createPasskey(credentials: CredentialsContainer | undefined, creation: PasskeyCreation): Promise<Record<string, unknown> | null> {
  const publicKey = creationOptions(creation);
  if (credentials === undefined || publicKey === null) return null;
  try {
    return registrationJson(await credentials.create({ publicKey }));
  } catch {
    return null;
  }
}
